import { useState, useMemo } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Trash2 } from "lucide-react";
import { toast } from "sonner";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { trpc, trpcClient } from "@/utils/trpc";

export const Route = createFileRoute("/_protected/history")({
  component: HistoryComponent,
});

function formatDate(date: Date | string): string {
  const d = new Date(date);
  return d.toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function formatTime(date: Date | string): string {
  const d = new Date(date);
  return d.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
  });
}

function HistoryComponent() {
  const [templateFilter, setTemplateFilter] = useState<string>("all");

  const sessionsQuery = useQuery(
    trpc.sessions.list.queryOptions({ limit: 100 })
  );

  const templatesQuery = useQuery(trpc.templates.list.queryOptions());

  const deleteMutation = useMutation({
    mutationFn: async (sessionId: string) => {
      return await trpcClient.sessions.delete.mutate({ sessionId });
    },
    onSuccess: () => {
      sessionsQuery.refetch();
      toast.success("Workout deleted");
    },
    onError: (error) => {
      toast.error(error.message || "Failed to delete workout");
    },
  });

  const handleDelete = (sessionId: string, templateName: string) => {
    if (window.confirm(`Delete this ${templateName} workout? This cannot be undone.`)) {
      deleteMutation.mutate(sessionId);
    }
  };

  // Filter sessions by template
  const filteredSessions = useMemo(() => {
    if (!sessionsQuery.data) return [];

    if (templateFilter === "all") {
      return sessionsQuery.data;
    }

    return sessionsQuery.data.filter(
      (session) => session.templateId === templateFilter
    );
  }, [sessionsQuery.data, templateFilter]);

  return (
    <div className="container mx-auto max-w-3xl px-4 py-6">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold">Workout History</h1>
        <Link to="/" className="text-sm text-muted-foreground hover:text-foreground">
          Back to Dashboard
        </Link>
      </div>

      {/* Filter */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-muted-foreground mb-1">
          Workout Type
        </label>
        <select
          value={templateFilter}
          onChange={(e) => setTemplateFilter(e.target.value)}
          className="w-full max-w-xs px-3 py-2 bg-background border border-input rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-ring"
        >
          <option value="all">All Workout Types</option>
          {templatesQuery.data?.map((template) => (
            <option key={template.id} value={template.id}>
              {template.name}
            </option>
          ))}
        </select>
      </div>

      {/* Results count */}
      {sessionsQuery.data && (
        <p className="text-sm text-muted-foreground mb-4">
          Showing {filteredSessions.length} of {sessionsQuery.data.length} sessions
        </p>
      )}

      {/* Loading state */}
      {sessionsQuery.isLoading && (
        <div className="space-y-3">
          {[1, 2, 3, 4, 5].map((i) => (
            <Card key={i}>
              <CardHeader>
                <div className="h-5 w-32 bg-muted animate-pulse rounded" />
              </CardHeader>
              <CardContent>
                <div className="h-4 w-48 bg-muted animate-pulse rounded" />
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Error state */}
      {sessionsQuery.error && (
        <Card>
          <CardContent className="py-6">
            <p className="text-destructive">
              Failed to load sessions: {sessionsQuery.error.message}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Empty state */}
      {filteredSessions.length === 0 && sessionsQuery.data && (
        <Card>
          <CardContent className="py-6">
            <p className="text-muted-foreground">
              {sessionsQuery.data.length === 0
                ? "No workouts recorded yet."
                : "No workouts match the selected filter."}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Sessions list */}
      {filteredSessions.length > 0 && (
        <div className="space-y-3">
          {filteredSessions.map((session) => (
            <Link
              key={session.id}
              to="/session/$sessionId"
              params={{ sessionId: session.id }}
            >
              <Card className="hover:bg-muted/50 transition-colors cursor-pointer">
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <span>{session.template.name}</span>
                    <div className="flex items-center gap-2">
                      {!session.completedAt && (
                        <>
                          <span className="text-xs font-normal bg-yellow-500/20 text-yellow-600 dark:text-yellow-400 px-2 py-0.5 rounded">
                            In Progress
                          </span>
                          <Button
                            variant="ghost"
                            size="icon-sm"
                            className="text-muted-foreground hover:text-destructive"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              handleDelete(session.id, session.template.name);
                            }}
                            disabled={deleteMutation.isPending}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </>
                      )}
                    </div>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center gap-4 text-sm text-muted-foreground">
                    <span>{formatDate(session.startedAt)}</span>
                    <span>{formatTime(session.startedAt)}</span>
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
